import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/components/AuthProvider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Check, X, DollarSign, Calendar, Mail, Phone, MessageSquare, Tag, CheckCircle2, AlertCircle, Trash2 } from "lucide-react";
import { useCurrency } from "@/hooks/use-currency";
import { useLocalized } from "@/hooks/useLocalized";
import { LocalizedContent } from "@/types";

interface Booking {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  total_amount: number;
  deposit_amount?: number;
  booking_status: string;
  payment_status: string;
  notes: string;
  created_at: string;
  services: {
    name: LocalizedContent;
    category: string;
  };
  name: LocalizedContent;
  category: string;
  promo_redemption?: {
    id: string;
    discount_amount: number;
    customer_email: string;
    promo_codes?: {
      code: string;
      discount_type: string;
      discount_value: number;
    };
  } | null;
}

const BOOKING_STATUSES = [
  'pending', 'confirmed', 'completed', 'cancelled'
];

const PAYMENT_STATUSES = [
  'pending', 'partial', 'paid', 'refunded'
];

export function BookingsManager() {
  const { formatPrice } = useCurrency();
  const { getLocalized } = useLocalized();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { role: userRole } = useAuth();


  // Deposit Dialog State
  const [isDepositDialogOpen, setIsDepositDialogOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [depositAmount, setDepositAmount] = useState("");

  // Manage Booking Dialog State
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const [activeBooking, setActiveBooking] = useState<Booking | null>(null);

  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          *,
          time_slots(slot_date, start_time, end_time)
        `)
        .order('created_at', { ascending: false });

      if (bookingsError) throw bookingsError;

      const rows = bookingsData || [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serviceIds = Array.from(new Set(rows.map((b: any) => b.service_id).filter(Boolean)));

      let servicesMap: Record<string, { name: LocalizedContent; category: string }> = {};

      if (serviceIds.length > 0) {
        const { data: servicesData } = await supabase
          .from('services')
          .select('id, name, category')
          .in('id', serviceIds);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (servicesData || []).forEach((s: any) => {
          servicesMap[s.id] = { name: s.name, category: s.category };
        });
      }

      // Fetch promo code redemptions for bookings
      const bookingIds = rows.map((b: any) => b.id);
      let redemptionsMap: Record<string, any> = {};

      if (bookingIds.length > 0) {
        // Fetch promo code redemptions
        const { data: promoRedemptionsData } = await supabase
          .from('promo_code_redemptions')
          .select('*, promo_codes(code, discount_type, discount_value)')
          .in('booking_id', bookingIds);

        (promoRedemptionsData || []).forEach((r: any) => {
          redemptionsMap[r.booking_id] = {
            ...r,
            code_type: 'PROMO'
          };
        });

        // Fetch referral code redemptions
        const { data: referralRedemptionsData } = await supabase
          .from('referral_code_redemptions')
          .select(`
            *,
            referral_codes(code, discount_type, discount_value),
            salespeople(name)
          `)
          .in('booking_id', bookingIds);

        (referralRedemptionsData || []).forEach((r: any) => {
          redemptionsMap[r.booking_id] = {
            ...r,
            code_type: 'REFERRAL',
            // Map to same structure as promo for consistency
            promo_codes: r.referral_codes
          };
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const enriched = rows.map((b: any) => ({
        ...b,
        services: servicesMap[b.service_id] || { name: 'Unknown service', category: 'unknown' },
        promo_redemption: redemptionsMap[b.id] || null,
      }));

      setBookings(enriched);

      // Update active booking if dialog is open
      if (selectedBookingId) {
        const updated = enriched.find(b => b.id === selectedBookingId);
        if (updated) setActiveBooking(updated);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast({
        title: "Error",
        description: "Failed to fetch bookings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId: string, status: string, type: 'booking' | 'payment') => {
    // If marking as partial, open the deposit dialog
    if (status === 'partial' && type === 'payment') {
      setSelectedBookingId(bookingId);
      setDepositAmount("");
      setIsDepositDialogOpen(true);
      return;
    }

    try {
      const updateField = type === 'booking' ? 'booking_status' : 'payment_status';
      const { error } = await supabase
        .from('bookings')
        .update({ [updateField]: status })
        .eq('id', bookingId);

      if (error) throw error;

      toast({
        title: "Status updated",
        description: `${type === 'booking' ? 'Booking' : 'Payment'} status updated to ${status}`,
      });

      fetchBookings();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const handleDepositSubmit = async () => {
    if (!selectedBookingId) return;

    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount < 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid positive number for the deposit.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('bookings')
        .update({
          payment_status: 'partial',
          deposit_amount: amount
        })
        .eq('id', selectedBookingId);

      if (error) throw error;

      toast({
        title: "Deposit Recorded",
        description: `Booking updated to Partial payment with deposit of ${formatPrice(amount)}`,
      });

      setIsDepositDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error('Error updating deposit:', error);
      toast({
        title: "Error",
        description: "Failed to update deposit amount",
        variant: "destructive",
      });
    }
  };

  const confirmDeleteBooking = (bookingId: string) => {
    setBookingToDelete(bookingId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteBooking = async () => {
    if (!bookingToDelete) return;

    try {
      // First delete associated redemptions to avoid constraint errors if no cascade
      await supabase.from('promo_code_redemptions').delete().eq('booking_id', bookingToDelete);
      await supabase.from('referral_code_redemptions').delete().eq('booking_id', bookingToDelete);

      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', bookingToDelete);

      if (error) throw error;

      toast({
        title: "Booking Deleted",
        description: "The booking has been permanently removed.",
      });

      fetchBookings();
    } catch (error: any) {
      console.error('Error deleting booking:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete booking",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
    }
  };

  const getStatusColor = (status: string, type: 'booking' | 'payment') => {
    if (type === 'booking') {
      switch (status) {
        case 'confirmed': return 'bg-green-100 text-green-800 border-green-200';
        case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
        default: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      }
    } else {
      switch (status) {
        case 'paid': return 'bg-green-100 text-green-800 border-green-200';
        case 'partial': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'refunded': return 'bg-red-100 text-red-800 border-red-200';
        default: return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    }
  };

  const activeBookings = bookings.filter(b => ['pending', 'confirmed'].includes(b.booking_status));
  const completedBookings = bookings.filter(b => b.booking_status === 'completed');
  const cancelledBookings = bookings.filter(b => b.booking_status === 'cancelled');

  const openActionDialog = (booking: Booking) => {
    setActiveBooking(booking);
    setSelectedBookingId(booking.id);
    setIsActionDialogOpen(true);
  };

  const renderBookingCard = (booking: Booking) => (
    <Card key={booking.id} className="overflow-hidden border-l-4 shadow-sm hover:shadow-md transition-shadow"
      style={{
        borderLeftColor: booking.booking_status === 'confirmed' ? '#22c55e' :
          booking.booking_status === 'pending' ? '#eab308' :
            booking.booking_status === 'cancelled' ? '#ef4444' : '#3b82f6'
      }}>
      <CardHeader className="bg-muted/30 pb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-xl font-bold">{booking.customer_name}</CardTitle>
              <Badge variant="outline" className={getStatusColor(booking.booking_status, 'booking')}>
                {booking.booking_status}
              </Badge>
            </div>
            <CardDescription className="flex items-center gap-2 text-base">
              <span className="font-semibold text-foreground">{getLocalized(booking.services.name)}</span>
              <Badge variant="secondary" className="text-xs">{booking.services.category}</Badge>
            </CardDescription>
          </div>
          <div className="text-right text-sm text-muted-foreground flex items-center gap-1 bg-background px-2 py-1 rounded border">
            <Calendar className="h-4 w-4" />
            {new Date(booking.created_at).toLocaleDateString()}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6 grid gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Mail className="h-4 w-4" />
            </div>
            <div className="overflow-hidden">
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="text-sm font-medium truncate" title={booking.customer_email}>{booking.customer_email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Phone className="h-4 w-4" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Phone</p>
              {booking.customer_phone ? (
                <a
                  href={`https://wa.me/${booking.customer_phone.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium hover:text-primary hover:underline"
                >
                  {booking.customer_phone}
                </a>
              ) : (
                <p className="text-sm font-medium">N/A</p>
              )}
            </div>
          </div>

          {/* Session Date & Time */}
          {booking.time_slots && (
            <div className="flex items-center gap-3 p-2 rounded-lg bg-blue-50 border border-blue-200">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                <Calendar className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs text-blue-700 font-medium">Session Date & Time</p>
                <p className="text-sm font-bold text-blue-600">
                  {new Date(booking.time_slots.slot_date).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
                <p className="text-xs text-blue-600">
                  {booking.time_slots.start_time.substring(0, 5)} - {booking.time_slots.end_time.substring(0, 5)}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <DollarSign className="h-4 w-4" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Payment</p>
              <div className="flex flex-col">
                {booking.promo_redemption ? (
                  <div className="space-y-0.5">
                    <div className="text-xs text-muted-foreground">
                      Original: <span className="line-through">{formatPrice(booking.total_amount + booking.promo_redemption.discount_amount)}</span>
                    </div>
                    <div className="text-xs text-green-600">
                      Discount: -{formatPrice(booking.promo_redemption.discount_amount)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold">{formatPrice(booking.total_amount)}</span>
                      <Badge variant="outline" className={`${getStatusColor(booking.payment_status, 'payment')} text-[10px] px-1 py-0 h-4`}>
                        {booking.payment_status}
                      </Badge>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">{formatPrice(booking.total_amount)}</span>
                    <Badge variant="outline" className={`${getStatusColor(booking.payment_status, 'payment')} text-[10px] px-1 py-0 h-4`}>
                      {booking.payment_status}
                    </Badge>
                  </div>
                )}
                {booking.payment_status === 'partial' && booking.deposit_amount !== undefined && (
                  <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                    <p className="text-green-600">Paid: {formatPrice(booking.deposit_amount)}</p>
                    <p className="text-red-500">Left: {formatPrice(booking.total_amount - (booking.deposit_amount || 0))}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {booking.promo_redemption && (
            <div className={`md:col-span-2 lg:col-span-1 flex items-start gap-3 p-2 rounded-lg border ${booking.promo_redemption.code_type === 'REFERRAL'
              ? 'bg-blue-50 border-blue-200'
              : 'bg-green-50 border-green-200'
              }`}>
              <Tag className={`h-4 w-4 mt-1 ${booking.promo_redemption.code_type === 'REFERRAL' ? 'text-blue-600' : 'text-green-600'
                }`} />
              <div className="overflow-hidden">
                <p className={`text-xs font-medium ${booking.promo_redemption.code_type === 'REFERRAL' ? 'text-blue-700' : 'text-green-700'
                  }`}>
                  {booking.promo_redemption.code_type === 'REFERRAL' ? 'Referral Code' : 'Promo Code'}
                </p>
                <p className={`text-sm font-bold ${booking.promo_redemption.code_type === 'REFERRAL' ? 'text-blue-600' : 'text-green-600'
                  }`}>
                  {booking.promo_redemption.promo_codes?.code}
                </p>
                <p className={`text-xs ${booking.promo_redemption.code_type === 'REFERRAL' ? 'text-blue-600' : 'text-green-600'
                  }`}>
                  -{formatPrice(booking.promo_redemption.discount_amount)}
                </p>
                {booking.promo_redemption.code_type === 'REFERRAL' && booking.promo_redemption.salespeople && (
                  <p className="text-xs text-blue-500 mt-0.5">
                    By: {booking.promo_redemption.salespeople.name}
                  </p>
                )}
              </div>
            </div>
          )}

          {booking.notes && (
            <div className="md:col-span-2 lg:col-span-1 flex items-start gap-3 p-2 rounded-lg bg-muted/30">
              <MessageSquare className="h-4 w-4 text-muted-foreground mt-1" />
              <div>
                <p className="text-xs text-muted-foreground">Notes</p>
                <p className="text-sm whitespace-pre-wrap">{booking.notes}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="bg-muted/10 border-t p-4 flex justify-between items-center">
        <p className="text-xs text-muted-foreground italic">
          {['completed', 'cancelled'].includes(booking.booking_status) ?
            `This booking is ${booking.booking_status} and cannot be modified.` :
            "Use the manage button to update status or record payments."
          }
        </p>
        {!['completed', 'cancelled'].includes(booking.booking_status) && (
          <Button size="sm" onClick={() => openActionDialog(booking)} className="gap-2">
            Manage Booking
          </Button>
        )}
        {userRole === 'super_admin' && (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => confirmDeleteBooking(booking.id)}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        )}
      </CardFooter>
    </Card>
  );

  const renderEmptyState = (type: string) => (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
          <AlertCircle className="h-6 w-6" />
        </div>
        <h3 className="text-lg font-semibold mb-1">No {type} bookings found</h3>
        <p className="text-sm">There are no bookings in this category yet.</p>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Booking Management</h2>
          <p className="text-muted-foreground mt-1">Manage and track your photography sessions.</p>
        </div>
        <div className="flex items-center gap-2 bg-muted p-1 rounded-lg text-sm font-medium">
          <span className="px-3 py-1 bg-background shadow-sm rounded-md">{bookings.length} Total</span>
          <span className="px-3 py-1 text-muted-foreground">{activeBookings.length} Active</span>
        </div>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-[400px]">
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4 mt-6">
          {activeBookings.length > 0 ? activeBookings.map(renderBookingCard) : renderEmptyState('active')}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4 mt-6">
          {completedBookings.length > 0 ? completedBookings.map(renderBookingCard) : renderEmptyState('completed')}
        </TabsContent>

        <TabsContent value="cancelled" className="space-y-4 mt-6">
          {cancelledBookings.length > 0 ? cancelledBookings.map(renderBookingCard) : renderEmptyState('cancelled')}
        </TabsContent>

        <TabsContent value="all" className="space-y-4 mt-6">
          {bookings.length > 0 ? bookings.map(renderBookingCard) : renderEmptyState('any')}
        </TabsContent>
      </Tabs>

      {/* MANAGE BOOKING DIALOG */}
      <Dialog open={isActionDialogOpen} onOpenChange={setIsActionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Booking Actions</DialogTitle>
            <DialogDescription>
              Update status or record payments for {activeBooking?.customer_name}.
            </DialogDescription>
          </DialogHeader>

          {activeBooking && (
            <div className="space-y-6 py-4">
              {/* Status Section */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Booking Status</h4>
                <div className="flex flex-wrap gap-2">
                  {activeBooking.booking_status === 'pending' && (
                    <Button
                      size="sm"
                      onClick={() => updateBookingStatus(activeBooking.id, 'confirmed', 'booking')}
                      className="bg-green-600 hover:bg-green-700 text-white gap-1"
                    >
                      <Check className="h-4 w-4" /> Confirm Booking
                    </Button>
                  )}

                  {['pending', 'confirmed'].includes(activeBooking.booking_status) && (
                    <>
                      <div className="w-full flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={activeBooking.payment_status !== 'paid'}
                          onClick={() => updateBookingStatus(activeBooking.id, 'completed', 'booking')}
                          className="gap-1 w-full"
                        >
                          <CheckCircle2 className="h-4 w-4" /> Complete Session
                        </Button>
                        {activeBooking.payment_status !== 'paid' && (
                          <p className="text-[10px] text-red-500 flex items-center gap-1 px-1">
                            <AlertCircle className="h-3 w-3" /> Payment must be "Paid" before completing.
                          </p>
                        )}
                      </div>

                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => updateBookingStatus(activeBooking.id, 'cancelled', 'booking')}
                        className="gap-1 w-full"
                      >
                        <X className="h-4 w-4" /> Cancel Booking
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Payment Section */}
              <div className="space-y-3 pt-2 border-t">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Payment Actions</h4>
                <div className="grid grid-cols-2 gap-2">
                  {activeBooking.payment_status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateBookingStatus(activeBooking.id, 'partial', 'payment')}
                        className="border-yellow-500 text-yellow-600 hover:bg-yellow-50 h-9"
                      >
                        Record Deposit
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateBookingStatus(activeBooking.id, 'paid', 'payment')}
                        className="bg-green-600 hover:bg-green-700 text-white h-9"
                      >
                        Mark as Paid
                      </Button>
                    </>
                  )}

                  {activeBooking.payment_status === 'partial' && (
                    <Button
                      size="sm"
                      onClick={() => updateBookingStatus(activeBooking.id, 'paid', 'payment')}
                      className="bg-green-600 hover:bg-green-700 text-white col-span-2 h-9"
                    >
                      Finalize Payment (Mark Paid)
                    </Button>
                  )}

                  {activeBooking.payment_status === 'paid' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateBookingStatus(activeBooking.id, 'refunded', 'payment')}
                      className="border-red-200 text-red-600 hover:bg-red-50 col-span-2 h-9"
                    >
                      Issue Refund
                    </Button>
                  )}
                </div>
                {activeBooking.payment_status === 'refunded' && (
                  <p className="text-sm text-center text-muted-foreground italic py-2">
                    Payment has been refunded.
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsActionDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDepositDialogOpen} onOpenChange={setIsDepositDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Deposit Payment</DialogTitle>
            <DialogDescription>
              Enter the amount paid as a deposit for this booking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deposit_amount">Deposit Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                <Input
                  id="deposit_amount"
                  placeholder="0.00"
                  type="number"
                  className="pl-7"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDepositDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDepositSubmit}>Confirm Deposit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the booking and remove data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBooking} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}